// CommentReplies.jsx
import React, { useEffect, useState, useCallback} from "react";
import api from "./api";
import Icons from "./Icons";
import FollowButton from "./FollowButton";

import "./CommentReplies.css";

//Responsible for handling replies for a comment on a Curu Chat Post

export default function CommentReplies({ postId, parentAnswer, currentUser }) {
  //List of replies for that parent
  const [replies, setReplies] = useState([]);
  //New reply input
  const [newReply, setNewReply] = useState("");
  //Loading & error flags
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  //ID of parent comment
  const parentId = parentAnswer?.id;

  // FETCH REPLIES FOR A SPECIFIC COMMENT
  const fetchReplies = useCallback(async () => {
    if (!postId || !parentId) return;
    setLoading(true);
    setError("");

    try {
      const res = await api.get(`/comunity/post/${postId}`);
      const all = res?.data?.answers || [];
      const parentFromApi = all.find((a) => String(a?.id) === String(parentId));
      const apiReplies = Array.isArray(parentFromApi?.answer_replies)
        ? parentFromApi.answer_replies
        : [];
      const localReplies =
        Array.isArray(parentAnswer?.replies) || Array.isArray(parentAnswer?.children)
          ? [...(parentAnswer?.replies || parentAnswer?.children || [])]
          : [];

      const byId = new Map();
      for (const r of [...apiReplies, ...localReplies]) {
        if (r) byId.set(r.id ?? `tmp-${Math.random()}`, r);
      }
      setReplies(Array.from(byId.values()));
    } catch (e) {
      console.error("Failed to fetch replies", e);
      setError("Failed to load replies.");
    } finally {
      setLoading(false);
    }
  }, [postId, parentId, parentAnswer]); 

  // Run whenever parentId or postId changes
  useEffect(() => {
      fetchReplies();
    }, [fetchReplies]);

  //SUBMIT A REPLY
  const handleAddReply = async () => {
    const message = newReply.trim();

    if (!message || message.length < 10) {
      setError("Reply cannot be empty or less than 10 character.");
      return;
    }

    const payload = { message, answer_id: Number(parentId) };

    //This is used for debugging purposes (uncomment if required)
    // console.log("sendAnswersReply API Request:", {
    //   communityId: postId,
    //   endpoint: `comunity/post/${postId}/answer`,
    //   requestBody: payload,
    // });

    try {
      const res = await api.post(
        `/comunity/post/${postId}/answer`,
        payload,
        { withCredentials: true } //If API relies on cookies
      );

      //Displays in ui appropriately
      const returned = res?.data ?? {};
      const normalized = {
        id: returned.id ?? returned.pk ?? `tmp-${Date.now()}`,
        message: returned.message ?? message,
        user: returned.user ?? currentUser ?? { username: "You", photo: null },
        created_ago: returned.created_ago ?? "Just now",
        answer_id: Number(parentId),
      };

      //Append the normalized reply
      setReplies((prev) => [...prev, normalized]);
      setNewReply("");
      setError("");
    } catch (e) {
      console.error("Failed to add reply", e);
      setError("Failed to add reply.");
    }
  };

  return (
    <div className="reply-thread">
      {/* Reply setup */}
      <div className="reply-composer">
        <input
          type="text"
          className="form-control custom-input"
          placeholder="Write a reply…"
          value={newReply}
          onChange={(e) => setNewReply(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAddReply()}
        />
        <button className="btn btn-primary" onClick={handleAddReply}>
          Reply
        </button>
      </div>

      {loading && <div className="small text-muted">Loading replies…</div>}
      {error && <div className="small text-danger">{error}</div>}

      {/* Reply List */}
      {replies.map((r, idx) => (
        <div key={r?.id ?? `reply-${parentId}-${idx}`} className="reply-item">
          <div className="reply-row">
            {/*Profile*/}
            <div className="reply-profile">
              {r?.user?.photo ? (
                <img
                  src={r.user.photo}
                  alt={r.user?.username}
                  className="reply-avatar-img"
                />
              ) : (
                <Icons.Profile className="reply-avatar" />
              )}
            </div>

            {/* Reply Content */}
            <div className="reply-content">
              <div className="reply-header">
                <span className="reply-username">{r?.user?.username}</span>
                <FollowButton user={r?.user} />
              </div>
              <div className="reply-date">{r?.created_ago}</div>

              <div className="reply-message">{r?.message}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
